import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Chip, ProgressBar, Icon, RingChart } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { formatDiamonds, isNum } from '../shared/format';

/**
 * Module 2 — A2.3/A2.4/A2.5 milestone tracker sub-views.
 *
 * Exposed via `?ms=t14|t30|grad` on /tcn/me/new-creators (mirrors the M3/M4
 * `?view=` pattern). Each view renders one focused artboard for the whole
 * onboarding cohort:
 *   - t14  → A2.3: valid LIVE days / 5 in first 14 days.
 *   - t30  → A2.4: LIVE days / 15 + LIVE hours / 45 in first 30 days.
 *   - grad → A2.5: graduation status + time proxy. (Diamonds toward graduation
 *            are AES-encrypted under R2, so the exact ring lives on the
 *            per-creator decrypted profile — here we show status + clear target.)
 */
const T14_DAYS = 5;
const T30_DAYS = 15;
const T30_HOURS = 45;

// status string → { chip: semantic key for <Chip>, color: css for ProgressBar }
const STATUS = {
  Passed: { chip: 'green', color: TCN.green },
  Graduated: { chip: 'diamond', color: TCN.diamond },
  'On track': { chip: 'cyan', color: TCN.cyan },
  'On pace': { chip: 'cyan', color: TCN.cyan },
  'At risk': { chip: 'amber', color: TCN.amber },
  Missed: { chip: 'red', color: TCN.red },
  Critical: { chip: 'red', color: TCN.red },
  'Not on track': { chip: 'red', color: TCN.red },
};
const statusOf = (s) => STATUS[s] || { chip: 'cyan', color: TCN.cyan };

const RowHeader = ({ creator, dayOf, status, t }) => {
  const handle = creator.tiktok_username
    ? `@${creator.tiktok_username}`
    : creator.display_name || '—';
  const st = statusOf(status);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Avatar name={creator.display_name || handle} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: TCN.text,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {handle}
        </div>
        <div style={{ fontSize: 11, color: TCN.textMute, marginTop: 2 }}>
          {isNum(creator.days_since_joining) && creator.days_since_joining > dayOf
            ? t('tcn.m2.ms.windowClosed', 'T+{{n}} window closed', { n: dayOf })
            : t('tcn.m2.ms.dayOf', 'Day {{n}} of {{total}}', {
                n: creator.days_since_joining ?? '?',
                total: dayOf,
              })}
        </div>
      </div>
      {status && (
        <Chip color={st.chip} size="sm" solid={status === 'Critical'}>
          {status === 'Critical' && '⚠ '}
          {t(`tcn.m2.status.${status.replace(/\s+/g, '_').toLowerCase()}`, status)}
        </Chip>
      )}
    </div>
  );
};

const card = (color) => ({
  padding: 14,
  background: TCN.surface1,
  border: `1px solid ${color}44`,
  borderRadius: 14,
  marginBottom: 10,
});

const metricLabel = {
  fontSize: 10,
  color: TCN.textMute,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const MilestoneTracker = ({ view, creators = [], gradTarget = null, decDiamonds = {} }) => {
  const { t } = useTranslation();

  if (!creators.length) {
    return (
      <div style={{ textAlign: 'center', padding: 24, color: TCN.textDim, fontSize: 13 }}>
        {t('tcn.m2.ms.empty', 'No creators in this milestone window.')}
      </div>
    );
  }

  /* A2.3 — T+14 */
  if (view === 't14') {
    return (
      <div>
        {creators.map((c) => {
          const st = statusOf(c.t14_status);
          const days = c.live_days_in_first_14;
          return (
            <div key={c.creator_id} style={card(st.color)}>
              <RowHeader creator={c} dayOf={14} status={c.t14_status} t={t} />
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, color: TCN.textMute, minWidth: 60 }}>
                  {t('tcn.m2.ms.liveDays', 'LIVE days')}
                </span>
                <div style={{ flex: 1 }}>
                  <ProgressBar value={days ?? 0} target={T14_DAYS} color={st.color} />
                </div>
                <span
                  dir="ltr"
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: st.color,
                    minWidth: 36,
                    textAlign: 'end',
                    fontVariantNumeric: 'tabular-nums',
                    fontFamily: TCN.fontMono,
                  }}
                >
                  {isNum(days) ? days : '—'}/{T14_DAYS}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  /* A2.4 — T+30 */
  if (view === 't30') {
    return (
      <div>
        {creators.map((c) => {
          const st = statusOf(c.t30_status);
          const d = c.live_days_in_first_30;
          const h = isNum(c.live_hours_in_first_30)
            ? Math.round(c.live_hours_in_first_30 * 10) / 10
            : null;
          return (
            <div key={c.creator_id} style={card(st.color)}>
              <RowHeader creator={c} dayOf={30} status={c.t30_status} t={t} />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                  marginTop: 14,
                }}
              >
                <div>
                  <div style={metricLabel}>{t('tcn.m2.ms.liveDays', 'LIVE days')}</div>
                  <div
                    dir="ltr"
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      marginTop: 2,
                      color: st.color,
                      fontVariantNumeric: 'tabular-nums',
                      fontFamily: TCN.fontMono,
                      textAlign: 'start',
                    }}
                  >
                    {isNum(d) ? d : '—'}
                    <span style={{ fontSize: 11, color: TCN.textDim }}>/{T30_DAYS}</span>
                  </div>
                  <ProgressBar value={d ?? 0} target={T30_DAYS} color={st.color} />
                </div>
                <div>
                  <div style={metricLabel}>{t('tcn.m2.ms.hours', 'Hours')}</div>
                  <div
                    dir="ltr"
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      marginTop: 2,
                      color: st.color,
                      fontVariantNumeric: 'tabular-nums',
                      fontFamily: TCN.fontMono,
                      textAlign: 'start',
                    }}
                  >
                    {isNum(h) ? h : '—'}
                    <span style={{ fontSize: 11, color: TCN.textDim }}>/{T30_HOURS}</span>
                  </div>
                  <ProgressBar value={h ?? 0} target={T30_HOURS} color={st.color} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  /* A2.5 — Graduation. Diamonds toward graduation are AES-encrypted under R2
     (super-admin blind); the AGENT is authorised on their own roster, so the
     parent bulk-decrypts them (decDiamonds) and we render the mockup ring
     (diamonds / target %). Falls back to a day-proxy bar + "Diamonds on
     profile" when decryption is unavailable. */
  const handleOf = (c) =>
    c.tiktok_username ? `@${c.tiktok_username}` : c.display_name || '—';
  return (
    <div>
      {creators.map((c) => {
        const st = statusOf(c.graduation_status);
        const target = c.graduation_diamond_target ?? gradTarget;
        const diamonds = decDiamonds[c.creator_id];
        const hasRing = isNum(diamonds) && isNum(target) && target > 0;
        const pct = hasRing ? Math.round((diamonds / target) * 100) : null;
        const ringColor = pct == null ? 'cyan' : pct >= 100 ? 'green' : pct >= 60 ? 'cyan' : 'amber';
        const dayProxy = Math.min(30, c.days_since_joining ?? 0);
        return (
          <div key={c.creator_id} style={card(st.color)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar name={c.display_name || handleOf(c)} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: TCN.text,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {handleOf(c)}
                </div>
                <div style={{ fontSize: 11, color: TCN.textMute, marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Icon name="diamond" size={11} color={TCN.diamond} />
                  {hasRing ? (
                    <span dir="ltr">
                      {t('tcn.m2.ms.gradOfTarget', '{{n}} of {{target}} diamonds', {
                        n: formatDiamonds(diamonds),
                        target: formatDiamonds(target),
                      })}
                    </span>
                  ) : isNum(target) ? (
                    t('tcn.m2.ms.gradTarget', '{{n}} ◆ target', { n: formatDiamonds(target) })
                  ) : (
                    t('tcn.m2.ms.gradTargetUnknown', 'Target pending')
                  )}
                </div>
                {c.graduation_status && (
                  <div style={{ marginTop: 6 }}>
                    <Chip color={st.chip} size="sm" solid={c.graduation_status === 'Graduated'}>
                      {t(`tcn.m2.status.${c.graduation_status.replace(/\s+/g, '_').toLowerCase()}`, c.graduation_status)}
                    </Chip>
                  </div>
                )}
              </div>
              {hasRing ? (
                <RingChart value={diamonds} target={target} size={64} stroke={7} color={ringColor} />
              ) : (
                <div style={{ width: 120 }}>
                  <ProgressBar value={dayProxy} target={30} color={st.color} />
                  <div style={{ fontSize: 10, color: TCN.textDim, marginTop: 4, textAlign: 'end' }}>
                    {t('tcn.m2.ms.diamondsOnProfile', 'Diamonds on profile')}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MilestoneTracker;
